import { useEffect, useRef, useState } from 'react';

// Animates a number from 0 → target with an ease-out curve.
// Respects prefers-reduced-motion (jumps straight to the value).
export function useCountUp(target = 0, { duration = 900, decimals = 0 } = {}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const end = Number(target) || 0;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || duration <= 0) {
      setValue(end);
      return undefined;
    }

    const from = fromRef.current;
    const startTs = performance.now();
    const factor = Math.pow(10, decimals);

    const tick = (now) => {
      const t = Math.min((now - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const current = from + (end - from) * eased;
      setValue(Math.round(current * factor) / factor);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = end;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, duration, decimals]);

  return value;
}

export default useCountUp;
